/**
 * Activity — `docs/activity.md` §6.1, §13, Board 1.
 *
 * One page, four tabs — **Calendar · Inbox · Tasks · All activity** — for
 * whoever the people button picks. The head is white: *Activity* and the
 * date line on the left; on the right the people button and **New ▾**
 * (**List · Board** before them on Tasks). Under it the tabs are
 * `RecordTabs`, imported unchanged (§13.4). What the page shows is
 * remembered per person per device on the server (§4.6).
 *
 * On a phone (Aaron, Oct 25, phone notes 1, 2, 8, 9) the page wears the
 * app's own top bar like every other page — ☰, the logo, the branch, search,
 * the avatar — and under it the page bar every phone page has: *Activity*,
 * then the view button (`ViewMenu`), the people button and **+**. The four
 * tabs are the record page's own bottom bar (`PhoneTabBar`), so the page's
 * box ends where the bar starts (`--activity-foot`).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ACTIVITY_TABS, CALENDAR_VIEWS, phoneCalendarViewOptions, phoneViewPatch, type ActivityGroup, type ActivityState,
  type ActivityTab, type ActivityView, type MeResponse, type PeopleDirectory,
} from '@rowted/shared'
import { RecordTabs } from '../record/record-tabs.js'
import { PhoneTabBar } from '../record/record-phone.js'
import { PageBar } from '../frame/page-bar.js'
import { cn } from '../lib/cn.js'
import { openNewRequest } from '../requests/open-request.js'
import { activityRequest, peopleQuery } from './api.js'
import { ActivityContext, type ActivityBadge, type ActivityContextValue } from './activity-context.js'
import { useActivityState } from './use-activity-state.js'
import { PeopleButton } from './people-button.js'
import { NewButton, type NewKind } from './new-button.js'
import { NewSheets, type NewSheetKind } from './new/new-sheets.js'
import { CalendarTab } from './calendar/calendar-tab.js'
import { CHANNELS, InboxTab } from './inbox/inbox-tab.js'
import { openThreadPrefill } from './inbox/actions.js'
import { TasksTab } from './tasks/tasks-tab.js'
import { AllTab } from './all/all-tab.js'
import { ACTIVITY_NEW_EVENT, type ActivityNewDetail } from './calendar/overlays.js'
import { Glyph } from './glyphs.js'
import { ACTIVITY_STOP_EVENT } from './calendar/phone-calendar.js'
import { StopPage } from './stop/stop-page.js'
import { TaskSheetHost } from './tasks/task-sheet.js'
import { uploadQueue } from './scope/upload-queue.js'
import { ACTIVITY_CHANGED_EVENT, announceBadge } from './nav-badge.js'
import { isTablet, useActivityLayout } from './use-layout.js'
import { useActivityNav } from './use-activity-history.js'
import { ViewMenu } from './view-menu.js'
import { ActivitySubContext, useActivitySubSlot } from './page-sub.js'
import { ActivityViewsContext, useActivityViewsSlot } from './page-views.js'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** *Thu, Oct 22*. */
export function dayWords(date: string): string {
  const day = new Date(`${date}T12:00:00Z`)
  return `${DAYS[day.getUTCDay()]}, ${MONTHS[day.getUTCMonth()]} ${day.getUTCDate()}`
}

const isTab = (value: string | null): value is ActivityTab =>
  value !== null && (ACTIVITY_TABS as readonly string[]).includes(value)

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const idsOf = (value: string | null): string[] => (value ?? '').split(',').filter((id) => UUID.test(id))
type LinkKey = 'date' | 'view' | 'people' | 'unassigned'

/** The remembered state with a deep link's day, view and people laid over it (§13.3). */
function linkedState(
  state: ActivityState | null, params: URLSearchParams, directory: PeopleDirectory | null,
): { shown: ActivityState | null; dropped: boolean; linked: LinkKey[] } {
  if (!state) return { shown: null, dropped: false, linked: [] }
  const linked: LinkKey[] = []
  let shown = state
  const onCalendar = (params.get('tab') ?? state.tab) === 'calendar'
  const date = params.get('date')
  if (onCalendar && date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    shown = { ...shown, calendar: { ...shown.calendar, date } }
    linked.push('date')
  }
  const view = params.get('view')
  if (onCalendar && view && (CALENDAR_VIEWS as readonly string[]).includes(view)) {
    shown = { ...shown, calendar: { ...shown.calendar, viewOne: view as ActivityView, viewSeveral: view as ActivityView } }
    linked.push('view')
  }
  let dropped = false
  const askedPeople = params.has('people') ? idsOf(params.get('people')) : null
  const askedUnits = params.has('unassigned') ? idsOf(params.get('unassigned')) : null
  if (askedPeople || askedUnits) {
    const seen = directory ? new Set(directory.people.map((p) => p.id)) : null
    const ids = (askedPeople ?? []).filter((id) => !seen || seen.has(id))
    dropped = Boolean(seen && askedPeople && ids.length < askedPeople.length)
    shown = { ...shown, people: { ...shown.people, groupId: undefined, ids: ids.length > 0 || askedUnits ? ids : shown.people.ids,
      unassigned: askedUnits ?? [] } }
    if (askedPeople) linked.push('people')
    if (askedUnits) linked.push('unassigned')
  }
  return { shown, dropped, linked }
}

export function ActivityPage({ me }: { me: MeResponse }) {
  const layout = useActivityLayout()
  const phone = layout === 'phone'
  const tablet = isTablet(layout)
  /*
   * The nav is the app's, drawn the same beside every page (Aaron, Nov 1: on a
   * touch screen it grew on Activity and shrank again on the next page). This
   * page used to ask for touch rows on its side and ☰ upright (Board 12 A, B).
   */
  const { state, change, error } = useActivityState()
  const [params, setParams] = useSearchParams()
  /* Each place in Activity is a history entry, so back steps out of them (Aaron, Oct 26 — `use-activity-history.ts`). */
  const nav = useActivityNav()
  const [directory, setDirectory] = useState<(PeopleDirectory & { groups: ActivityGroup[] }) | null>(null)
  const [badge, setBadge] = useState<ActivityBadge | null>(null)
  /* `menu` is the phone's New sheet (Board 16 D). */
  const [composing, setComposing] = useState<NewSheetKind | null>(null)
  const [prefill, setPrefill] = useState<Record<string, unknown> | null>(null)
  const [directoryTick, setDirectoryTick] = useState(0)
  const [badgeTick, setBadgeTick] = useState(0)
  /* The line under *Activity* on a phone, said by whichever tab is showing (`page-sub.tsx`, Aaron Oct 27). */
  const subSlot = useActivitySubSlot()
  /* Tasks' and All activity's saved views, as the phone page bar's tabs (`page-views.tsx`, Aaron Oct 27). */
  const viewsSlot = useActivityViewsSlot()

  useEffect(() => {
    let live = true
    activityRequest<PeopleDirectory & { groups: ActivityGroup[] }>('/activity/people')
      .then((answer) => { if (live) setDirectory(answer) })
      .catch(() => { /* the people button says Loading… until it answers */ })
    return () => { live = false }
  }, [directoryTick])

  /*
   * A deep link (§13.3) shows its day, view and people for this visit without
   * becoming what the page remembers; the first change the person makes takes
   * the link's words out of the address, and the remembered state carries on.
   */
  const { shown, dropped, linked } = useMemo(
    () => linkedState(state, params, directory), [state, params, directory])
  const [notice, setNotice] = useState<string | null>(null)
  useEffect(() => {
    if (!dropped) return undefined
    setNotice('Some people aren’t shown — you can’t see their calendars.')
    const hide = window.setTimeout(() => setNotice(null), 5000)
    return () => window.clearTimeout(hide)
  }, [dropped])
  const changeShown = useCallback((patch: Parameters<typeof change>[0], options?: Parameters<typeof change>[1]) => {
    const touches = linked.filter((key) => (key === 'people' || key === 'unassigned' ? Boolean(patch.people)
      : Boolean(patch.calendar && (key === 'date' ? patch.calendar.date !== undefined
        : patch.calendar.viewOne !== undefined || patch.calendar.viewSeveral !== undefined))))
    if (touches.length > 0) {
      setParams((current) => {
        const out = new URLSearchParams(current)
        for (const key of touches) out.delete(key)
        return out
      }, { replace: true })
    }
    change(patch, options)
  }, [change, linked, setParams])

  const people = useMemo(() => shown?.people.ids ?? [], [shown])
  const unassigned = useMemo(() => shown?.people.unassigned ?? [], [shown])

  useEffect(() => {
    if (!state) return
    let live = true
    const query = `/activity/badge?people=${peopleQuery(people)}&unassigned=${peopleQuery(unassigned)}`
    activityRequest<ActivityBadge>(query)
      .then((answer) => {
        if (!live) return
        setBadge(answer)
        announceBadge(answer)
      })
      .catch(() => { if (live) setBadge(null) })
    const every = window.setInterval(() => setBadgeTick((n) => n + 1), 60_000)
    const onChanged = () => setBadgeTick((n) => n + 1)
    window.addEventListener(ACTIVITY_CHANGED_EVENT, onChanged)
    return () => { live = false; window.clearInterval(every); window.removeEventListener(ACTIVITY_CHANGED_EVENT, onChanged) }
  }, [state, people, unassigned, badgeTick])

  /*
   * A stop tapped on the phone opens its page over the calendar (§10.3, Board 14) — `?stop=`, an
   * entry of its own, so back closes it and a reload opens it again (Aaron, Oct 26).
   */
  const stopId = nav.get('stop')
  const { push: pushNav } = nav
  useEffect(() => {
    const onStop = (event: Event) => pushNav({ stop: (event as CustomEvent<{ id: string }>).detail.id })
    window.addEventListener(ACTIVITY_STOP_EVENT, onStop)
    return () => window.removeEventListener(ACTIVITY_STOP_EVENT, onStop)
  }, [pushNav])
  /* Photos and notes a closed app left waiting go up as soon as Activity opens (§11.4). */
  useEffect(() => { uploadQueue() }, [])

  /* A stop card, a thread or the free time asks for a New sheet with what to start from (§9). */
  useEffect(() => {
    const onAsk = (event: Event) => {
      const detail = (event as CustomEvent<ActivityNewDetail>).detail
      setPrefill(detail.prefill ?? null)
      setComposing(detail.kind)
    }
    window.addEventListener(ACTIVITY_NEW_EVENT, onAsk)
    return () => window.removeEventListener(ACTIVITY_NEW_EVENT, onAsk)
  }, [])

  /* `?tab=` wins for this visit and is remembered; nothing else in a deep link overwrites the state (§13.3). */
  const asked = params.get('tab')
  useEffect(() => {
    if (state && isTab(asked) && asked !== state.tab) change({ tab: asked })
  }, [asked, state, change])
  const tab: ActivityTab = isTab(asked) ? asked : state?.tab ?? 'calendar'
  /*
   * Arriving with no `?tab=`, the page writes the tab it opened on into this entry: a tab switch
   * changes what is remembered, so an entry that said nothing would come back as the new tab.
   */
  const { replace: replaceNav } = nav
  useEffect(() => {
    if (state && !asked) replaceNav({ tab: state.tab })
  }, [state, asked, replaceNav])

  /*
   * Switching tabs is going somewhere: a new entry, so back comes back to the tab before (Aaron,
   * Oct 26). It starts from a bare address — a thread, a task, a day or the list engine's own
   * `saved` and `q` belong to the tab they were opened on, and that entry keeps them.
   */
  const choose = useCallback((next: string) => {
    if (!isTab(next) || next === (isTab(params.get('tab')) ? params.get('tab') : state?.tab)) return
    change({ tab: next })
    pushNav({ tab: next }, { fresh: true })
  }, [change, pushNav, params, state])

  const hasInbox = directory ? directory.people.some((p) => p.id === me.user.id && p.inbox) : true
  const canRequest = me.permissions.jobs === 'edit' || me.permissions.jobs === 'full'

  const value: ActivityContextValue | null = useMemo(() => (shown ? {
    me,
    state: shown,
    change: changeShown,
    directory,
    reloadDirectory: () => setDirectoryTick((n) => n + 1),
    people,
    unassigned,
    badge,
    reloadBadge: () => setBadgeTick((n) => n + 1),
    today: shown.calendar.dateSetOn,
  } : null), [me, shown, changeShown, directory, people, unassigned, badge])

  if (error) {
    return <div className="p-6 text-[13px] text-danger">{error}</div>
  }
  if (!value) {
    return <div className="p-6 text-[13px] text-text-muted">Loading…</div>
  }

  const tabs = [
    { key: 'calendar', label: 'Calendar', icon: 'calendar' as const },
    ...(hasInbox ? [{ key: 'inbox', label: 'Inbox', icon: 'inbox' as const, count: badge?.unread }] : []),
    { key: 'tasks', label: 'Tasks', icon: 'task' as const, count: badge?.dueOrLate },
    { key: 'all', label: 'All activity', icon: 'activity' as const },
  ]
  const shownTab: ActivityTab = tab === 'inbox' && !hasInbox ? 'calendar' : tab

  const onNew = (kind: NewKind) => {
    if (kind === 'request') openNewRequest()
    /* On the Inbox tab what is made starts from the open conversation (§9.2, Board 11 D). */
    else { setPrefill(shownTab === 'inbox' ? openThreadPrefill() : null); setComposing(kind) }
  }

  /*
   * The phone's view button, one per tab and alike on all of them (notes 2,
   * 8, 9): the Calendar's views and the Inbox's channel are the page's
   * state; Tasks' List · Board is the tab's own (each list remembers its
   * view), so Tasks puts its button in the slot beside it. All activity has
   * nothing to choose, so nothing is drawn, and the people button and **+**
   * stay where they were.
   */
  const viewMenu = shownTab === 'calendar'
    ? <ViewMenu label="Calendar view" value={value.state.calendar.phoneView} options={phoneCalendarViewOptions()}
        onChange={(view) => value.change(phoneViewPatch(view))} />
    : shownTab === 'inbox'
      ? <ViewMenu label="Texts or email" value={value.state.inbox.channel} options={CHANNELS}
          onChange={(channel) => value.change({ inbox: { channel } })} />
      : null

  return (
    <ActivityContext.Provider value={value}>
      <ActivitySubContext.Provider value={subSlot.say}>
      <ActivityViewsContext.Provider value={viewsSlot.hand}>
      {/* The calendar always fills the screen and scrolls inside its card (Board 12; Aaron, Oct 23). */}
      {/* Tasks fill it at least, so the rail and the Board's columns reach the bottom (Aaron, Oct 23). */}
      {/*
        * On a phone the box ends at the top of the bottom tab bar (58 px, its
        * rule and the home indicator): `--activity-foot`, which anything fixed
        * to the foot of the screen on any tab adds to its own offset.
        */}
      <div className={cn('flex min-w-0 flex-1 flex-col', shownTab === 'tasks' ? 'min-h-full' : 'min-h-0',
        shownTab === 'calendar' && 'h-full',
        phone && '[--activity-foot:calc(59px+env(safe-area-inset-bottom,0px))] pb-[var(--activity-foot)]')}>
        {phone ? (
          /* Aaron, Oct 25, note 1: the page bar every phone page has, under the app's own top bar. */
          /*
            * The title stays where the Contacts list's is on every tab and view (Aaron, Oct 27): a tab
            * with nothing to say keeps the sub's line, empty, rather than letting the title drop to the
            * middle of the bar and jump each time the line comes and goes.
            */
          <PageBar title="Activity" sub={subSlot.sub ?? '\u00a0'}
            tabs={viewsSlot.views?.tabs} activeTab={viewsSlot.views?.activeTab}
            onTabChange={viewsSlot.views?.onTabChange} onSaveView={viewsSlot.views?.onSaveView}
            actions={(
            <>
              <span id="activity-head-slot" className="contents" />
              {viewMenu}
              <PeopleButton layout="phone" />
              <button type="button" aria-label="New" onClick={() => { setPrefill(null); setComposing('menu') }}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-accent text-white">
                <Glyph name="plus" size={20} stroke={2.6} />
              </button>
            </>
          )} />
        ) : (
          <header className={cn('bg-surface px-6', tablet ? 'pb-2 pt-3' : 'pb-2.5 pt-3.5')}>
            <div className="flex items-center gap-2.5">
              <h1 className="m-0 text-[20px] font-bold tracking-[-.01em]">Activity</h1>
              <span className="truncate text-[12px] text-text-faint">
                {dayWords(value.today)} · {me.scope.label}
              </span>
              <span className="flex-1" />
              <span id="activity-head-slot" className="contents" />
              <PeopleButton layout={layout} />
              <NewButton tab={shownTab} onNew={onNew} canText={hasInbox} canRequest={canRequest} size={tablet ? 40 : 36} />
            </div>
          </header>
        )}
        {!phone && <RecordTabs tabs={tabs} active={shownTab} onChange={choose} />}
        <div className="flex min-h-0 flex-1 flex-col">
          {shownTab === 'calendar' && <CalendarTab />}
          {shownTab === 'inbox' && <InboxTab />}
          {shownTab === 'tasks' && <TasksTab />}
          {shownTab === 'all' && <AllTab />}
        </div>
        {stopId && <StopPage id={stopId} onClose={() => nav.close(['stop'])} />}
        {/* The Calendar's timed-task chip opens the Task sheet too (§8.2); the newest host answers. */}
        {shownTab === 'calendar' && <TaskSheetHost />}
        {/* Note 2: the record page's own bar, four cells and no More, the counts on Inbox and Tasks. */}
        {phone && (
          <PhoneTabBar tabs={tabs} cells={tabs.map((one) => one.key)} active={shownTab} onChange={choose}
            objectLabel="Activity" label="Activity" more={false} counts />
        )}
        {notice && (
          <div role="status" className="fixed bottom-[calc(24px+var(--activity-foot,0px))] left-1/2 z-[80] -translate-x-1/2 rounded-[10px] border border-line bg-surface px-4 py-2.5 text-[13px] font-medium text-text shadow-popover">
            {notice}
          </div>
        )}
        <NewSheets kind={composing} prefill={prefill} canText={hasInbox} canRequest={canRequest}
          onClose={() => { setComposing(null); setPrefill(null) }} />
      </div>
      </ActivityViewsContext.Provider>
      </ActivitySubContext.Provider>
    </ActivityContext.Provider>
  )
}
